/**
 * Wizard payload ↔ Strategies API DTO.
 *
 * `transformData()` in `@hooks/useFormSubmit` keeps producing the shape
 * PRD 02 §2 describes — that contract does not move. What this module
 * adds is a **whitelist**: it picks exactly the fields the API accepts
 * and nothing else. The drawer state carries bookkeeping of its own
 * (and a loaded strategy carries server-owned fields), and the DTO
 * refuses unknown keys, so spreading the payload straight into the
 * request would 422 on fields the operator cannot even see.
 *
 * Since CU-869fhuhy6 (brief v3) a strategy names ONE product
 * (`productId`), says whom it is compared against (`compareAgainst`, the
 * three modes that replaced the two switches of CU-869fh646w) and owns
 * its cadence (`whenItRuns`, the fields of the old schedule rule). The
 * face filters of CU-869fe6mf4 and the two switches are gone from the
 * wire on this side: the product says which of our rows the rule prices.
 *
 * Pure module: no React, no fetch. Unit-tested with `node --test`.
 */
import { factoryCadence, WEEKDAY_CODES } from './cadence.js'

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v))
const str = (v) => (v === null || v === undefined || v === '' ? null : String(v))
const list = (v) => (Array.isArray(v) ? v.filter(x => x !== null && x !== undefined && x !== '') : [])

/**
 * The axes whose values are Price Seeker ids, read back as numbers.
 *
 * They are stored as text — the engine compares them against
 * `str(id_master_provider)` and `str(id_site)` from the rate shop — so
 * the API hands back `["106"]` where the pickers hold `106`. Nothing
 * matched, the selector opened empty, and the next save wrote the
 * emptiness back: a strategy silently lost its market scope and its
 * competitor guard just by being edited (Curro, 2026-09-28).
 *
 * Only digits are converted. A value that is not a number is some
 * other kind of code and must survive untouched — dropping it would be
 * the same bug wearing a different hat.
 */
export const idList = (v) => list(v).map(x => (/^\d+$/.test(String(x)) ? Number(x) : x))

/**
 * A clock time as the server's `^HH:MM$` wants it, or `null`.
 * `<input type="time">` hands back `08:00:00` on Safari once a `step` is
 * set and `''` on Firefox while half-typed; a one-digit hour is padded.
 */
export const hhmm = (v) => {
	const m = /^(\d{1,2}):([0-5]\d)(?::.*)?$/.exec(String(v ?? '').trim())
	if (!m || Number(m[1]) > 23) return null
	return `${m[1].padStart(2, '0')}:${m[2]}`
}

/** Fields the server owns or derives; never sent back. */
export const SERVER_OWNED = [
	'id', 'status', 'mode', 'enabled_at', 'created_at', 'updated_at',
	'uid', 'enabled', 'show', 'reportsName',
	'timestampSorted', 'timestamp_create', 'timestamp_update',
	// Computed by the server for the list (CU-869fhuhy6): the product's
	// verdict and the sites the product no longer has.
	'product', 'sitesStale',
]

// ─── Compare against (criterion 12) ──────────────────────────────────────────

export const DEFAULT_COMPARE = Object.freeze({ mode: 'same', tariffType: null, insuranceId: null })
const COMPARE_MODES = ['same', 'every', 'specific']

/** `{ mode, tariffType, insuranceId }` → the wire: a mode that is not `specific` carries no axis. */
export function compareToServer(c) {
	const mode = COMPARE_MODES.includes(c?.mode) ? c.mode : 'same'
	return {
		mode,
		tariffType: mode === 'specific' && c?.tariffType != null ? !!c.tariffType : null,
		insuranceId: mode === 'specific' ? num(c?.insuranceId) : null,
	}
}

/** The wire → the wizard's compare block; absent (a strategy saved before) reads as `same`. */
export function compareFromServer(c) {
	if (!c || typeof c !== 'object') return { ...DEFAULT_COMPARE }
	const mode = COMPARE_MODES.includes(c.mode) ? c.mode : 'same'
	return {
		mode,
		tariffType: mode === 'specific' && c.tariffType != null ? !!c.tariffType : null,
		insuranceId: mode === 'specific' ? num(c.insuranceId) : null,
	}
}

// ─── When it runs (criterion 23) ─────────────────────────────────────────────

/**
 * The wizard's cadence → `WhenItRunsIn`: the fields of the schedule rule
 * (`ScheduleRuleIn` without name, priority, is_active, office_codes and,
 * since CU-869fkr1a9, without the pickup horizon — the pickup dates come
 * from the period) plus `reviewed` (UX v3 D19). Hours as `HH:MM`, weekdays
 * as the rule's codes.
 */
export function cadenceToServer(c) {
	const x = c || factoryCadence()
	const specific = x.frequencyKind === 'specific_hours'
	return {
		frequency_kind: specific ? 'specific_hours' : 'every_n_hours',
		frequency_value: specific
			? (Array.isArray(x.frequencyValue) ? x.frequencyValue : []).map(hhmm).filter(Boolean)
			: (num(x.frequencyValue) ?? 1),
		window_start: hhmm(x.windowStart) || '08:00',
		window_end: hhmm(x.windowEnd) || '20:00',
		weekdays: (x.weekdays || []).filter(d => WEEKDAY_CODES.includes(d)),
		pickup_hour: hhmm(x.pickupHour),
		dropoff_hour: hhmm(x.dropoffHour),
		reviewed: !!x.reviewed,
	}
}

/**
 * `whenItRuns` of a server row → the wizard's cadence. A strategy the
 * migration left without one (criterion 35) reads as the factory
 * proposal, unreviewed: the chip says so and the save makes it its own.
 * A horizon an older server still sends is ignored (CU-869fkr1a9).
 */
export function cadenceFromServer(w) {
	if (!w || typeof w !== 'object') return factoryCadence()
	const specific = w.frequency_kind === 'specific_hours'
	return {
		frequencyKind: specific ? 'specific_hours' : 'every_n_hours',
		frequencyValue: specific ? (Array.isArray(w.frequency_value) ? w.frequency_value.map(hhmm).filter(Boolean) : [])
		                         : (num(w.frequency_value) ?? 1),
		windowStart: hhmm(w.window_start) || '',
		windowEnd: hhmm(w.window_end) || '',
		weekdays: (w.weekdays || []).filter(d => WEEKDAY_CODES.includes(d)),
		pickupHour: hhmm(w.pickup_hour),
		dropoffHour: hhmm(w.dropoff_hour),
		reviewed: w.reviewed !== false,
	}
}

export function toServer(payload) {
	const strategy = payload.strategy || {}
	const dto = {
		name: payload.name,
		priority: num(payload.priority) ?? 50,
		// The one product this strategy prices (CU-869fhuhy6). Null only on
		// a migrated strategy saved off without one; the wizard blocks it.
		productId: str(payload.productId),
		compareAgainst: compareToServer(payload.compareAgainst),
		whenItRuns: cadenceToServer(payload.whenItRuns),
		period: {
			seasons: list(payload.period?.seasons).map(s => ({
				seasonFrom: s.seasonFrom, seasonTo: s.seasonTo,
			})),
			leadTimes: list(payload.period?.leadTimes).map(lt => ({
				leadFrom: num(lt.leadFrom), leadTo: num(lt.leadTo),
			})),
			weekdays: list(payload.period?.weekdays).map(num),
		},
		durations: list(payload.durations).map(d => ({
			durationFrom: num(d.durationFrom), durationTo: num(d.durationTo),
		})),
		compareByCategory: !!payload.compareByCategory,
		filterGroupOffices: list(payload.filterGroupOffices),
		// The sites of the product the rule is compared on (criterion 10):
		// inclusion only, a subset of the product's sites. An older
		// exclusion must already be converted (`siteScope.js`: the wizard
		// and "Align B to A"), or forcing false here saves "only Y".
		filterGroupSites: list(payload.filterGroupSites),
		filterGroupWebSuppliers: list(payload.filterGroupWebSuppliers),
		filterGroupProviders: list(payload.filterGroupProviders),
		filterProviders: list(payload.filterProviders),
		filterAcriss: list(payload.filterAcriss),
		filterCategories: list(payload.filterCategories),
		filterGroupOfficesExclusion: !!payload.filterGroupOfficesExclusion,
		filterGroupSitesExclusion: false,
		filterGroupWebSuppliersExclusion: !!payload.filterGroupWebSuppliersExclusion,
		filterGroupProvidersExclusion: !!payload.filterGroupProvidersExclusion,
		filterProvidersExclusion: !!payload.filterProvidersExclusion,
		filterAcrissExclusion: !!payload.filterAcrissExclusion,
		filterCategoriesExclusion: !!payload.filterCategoriesExclusion,
		strategy: strategy.type === 'ranking'
			? {
				type: 'ranking',
				targetPosition: num(strategy.targetPosition),
				keepPosition: !!strategy.keepPosition,
				// Only travels with the guard on: the API rejects a margin the guard would ignore.
				...(strategy.keepPosition
					? { keepPositionMargin: str(strategy.keepPositionMargin ?? null) }
					: {}),
			}
			: {
				type: 'market_trend',
				rankCap: num(strategy.rankCap),
				excludeOutliersPct: str(strategy.excludeOutliersPct ?? null),
			},
		tethers: list(payload.tethers),
		notes: payload.notes ?? null,
	}

	if (payload.undercut && (payload.undercut.value !== null && payload.undercut.value !== undefined)) {
		dto.undercut = {
			value: str(payload.undercut.value),
			type: payload.undercut.type ?? null,
			direction: num(payload.undercut.direction) ?? 0,
		}
	}
	if (payload.competitorRule) {
		dto.competitorRule = {
			neverBelow: list(payload.competitorRule.neverBelow),
			neverAbove: list(payload.competitorRule.neverAbove),
		}
	}
	const conditions = list(payload.advanced?.kpi_conditions)
	if (conditions.length) {
		dto.advanced = {
			kpi_conditions: conditions.map(c => ({ kpi: c.kpi, op: c.op, value: str(c.value) })),
		}
	}
	if (payload.tools?.smartAcrissPricing) {
		dto.tools = { smartAcrissPricing: true }
	}
	return dto
}

/**
 * Server row → what the wizard and the list expect.
 *
 * `enabled` is derived for the list's toggle, which is a boolean, while
 * `status` carries the three real states so the list can tell a draft
 * (never live) from a disabled one (live once, turned off). `product`
 * (`{ id, name, state, reason }`) and `sitesStale` travel through as the
 * server computed them for the table.
 */
export function fromServer(row) {
	const period = row.period || {}
	return {
		...row,
		enabled: row.status === 'enabled',
		productId: row.productId == null || row.productId === '' ? null : String(row.productId),
		compareAgainst: compareFromServer(row.compareAgainst),
		whenItRuns: cadenceFromServer(row.whenItRuns),
		sitesStale: idList(row.sitesStale),
		// The id axes, as numbers. They are stored as text — the engine
		// compares them against `str(id_master_provider)` and
		// `str(id_site)` from the rate shop — so the API hands back
		// `["106"]` where the pickers hold `106`. Nothing matched, the
		// selector opened empty, and the next save wrote that emptiness
		// back: a strategy lost its market scope and its competitor
		// guard by being opened (Curro, 2026-09-28).
		filterGroupSites: idList(row.filterGroupSites),
		filterGroupWebSuppliers: idList(row.filterGroupWebSuppliers),
		filterGroupProviders: idList(row.filterGroupProviders),
		filterProviders: idList(row.filterProviders),
		competitorRule: row.competitorRule
			? {
				...row.competitorRule,
				neverBelow: idList(row.competitorRule.neverBelow),
				neverAbove: idList(row.competitorRule.neverAbove),
			}
			: row.competitorRule,
		period: {
			seasons: period.seasons || [],
			leadTimes: period.leadTimes || [],
			weekdays: period.weekdays || [],
		},
		undercut: row.undercut
			? { ...row.undercut, value: num(row.undercut.value) }
			: null,
		advanced: row.advanced
			? { kpi_conditions: row.advanced.kpi_conditions.map(c => ({ ...c, value: num(c.value) })) }
			: null,
	}
}

/** Strip what the server owns, for a clone. The product and the cadence come along. */
export function forClone(row) {
	const out = { ...fromServer(row) }
	for (const key of SERVER_OWNED) delete out[key]
	out.name = null
	return out
}
